import { useLanguage } from "@/lib/language";
import { useApprovedTestimonials } from "@/lib/hooks";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { TestimonialDialog } from "./TestimonialDialog";
import {
  Phone,
  Mail,
  MessageCircle,
  MapPin,
  Clock,
  Star,
  Facebook,
  Instagram,
  Linkedin,
} from "lucide-react";
import NASysetmsLogo from "@/assets/na-systems.svg";

export function Footer() {
  const { t, language } = useLanguage();
  const { data: apiTestimonials = [], isLoading: testimonialsLoading } =
    useApprovedTestimonials();

  const handleNavigation = (path: string) => {
    const currentPath = window.location.pathname;
    const pathSegments = currentPath.split("/").filter(Boolean);
    const firstSegment = pathSegments[0];

    // Check if current path has language prefix
    const hasLanguagePrefix =
      firstSegment === "he" || firstSegment === "en" || firstSegment === "fr";

    const targetPath = hasLanguagePrefix
      ? `/${firstSegment}${path}`
      : `/${language}${path}`;

    // First scroll to top
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });

    // Then navigate
    window.history.pushState({}, "", targetPath);
    window.location.reload(); // Force reload to trigger route change
  };

  const contactMethods = [
    {
      icon: Phone,
      label: t("phone"),
      value: "+972-50-236-4585",
      href: "tel:+972502364585",
      color: "text-primary",
    },
    {
      icon: Mail,
      label: t("email"),
      value: "samantha@realestate-in-israel.com",
      href: "mailto:samantha@realestate-in-israel.com",
      color: "text-primary",
    },
    {
      icon: MessageCircle,
      label: t("whatsapp"),
      value: "+972-50-236-4585",
      href: "https://wa.me/972502364585",
      color: "text-green-600",
    },
  ];

  const officeHours = [
    {
      days: {
        en: "Sunday - Thursday",
        he: "ראשון - חמישי",
        fr: "Dimanche - Jeudi",
        ru: "Воскресенье - Четверг",
      },
      hours: "9:00 AM - 7:00 PM",
    },
    {
      days: {
        en: "Friday",
        he: "שישי",
        fr: "Vendredi",
        ru: "Пятница",
      },
      hours: "9:00 AM - 2:00 PM",
    },
    {
      days: {
        en: "Saturday",
        he: "שבת",
        fr: "Samedi",
        ru: "Суббота",
      },
      hours: {
        en: "Closed",
        he: "סגור",
        fr: "Fermé",
        ru: "Закрыто",
      },
    },
  ];

  const socialLinks = [
    {
      icon: Facebook,
      href: "https://www.facebook.com/samantha.azoulay1",
      label: "Facebook",
    },
    // {
    //   icon: Instagram,
    //   href: "https://instagram.com/samantha.azoulay.realestate",
    //   label: "Instagram",
    // },
    // {
    //   icon: Linkedin,
    //   href: "https://linkedin.com/in/samantha-azoulay",
    //   label: "LinkedIn",
    // },
  ];

  return (
    <footer id="contact" className="bg-card border-t border-border">
      {/* Main Footer Section */}
      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {/* Contact Methods */}
          <div className="lg:col-span-1">
            <h3 className="text-xl font-semibold text-foreground mb-6">
              {t("getInTouch")}
            </h3>
            <div className="space-y-4">
              {contactMethods.map((method, index) => {
                const Icon = method.icon;
                return (
                  <Card
                    key={index}
                    className="hover:shadow-md transition-shadow"
                  >
                    <CardContent className="p-4">
                      <a
                        href={method.href}
                        className="flex items-center gap-3 group"
                      >
                        <div
                          className={`w-10 h-10 rounded-lg bg-background flex items-center justify-center ${method.color}`}
                        >
                          <Icon size={20} />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">
                            {method.label}
                          </p>
                          <p className="font-medium text-foreground group-hover:text-primary transition-colors" dir="ltr">
                            {method.value}
                          </p>
                        </div>
                      </a>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Social Links */}
            <div className="mt-6">
              <h4 className="text-lg font-medium text-foreground mb-3">
                {t("followUs")}
              </h4>
              <div className="flex gap-3">
                {socialLinks.map((social, index) => {
                  const Icon = social.icon;
                  return (
                    <Button
                      key={index}
                      variant="outline"
                      size="icon"
                      asChild
                      className="hover:bg-primary hover:text-primary-foreground"
                    >
                      <a
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={social.label}
                      >
                        <Icon size={18} />
                      </a>
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Office Hours & Location */}
          <div className="lg:col-span-1">
            <h3 className="text-xl font-semibold text-foreground mb-6">
              {t("officeHours")}
            </h3>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-4">
                  <Clock size={20} className="text-primary" />
                  <h4 className="font-medium text-foreground">
                    {t("businessHours")}
                  </h4>
                </div>
                <div className="space-y-2">
                  {officeHours.map((schedule, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center"
                    >
                      <span className="text-sm text-muted-foreground">
                        {schedule.days[language]}
                      </span>
                      <span className="text-sm font-medium text-foreground">
                        {typeof schedule.hours === "string"
                          ? schedule.hours
                          : schedule.hours[language]}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="mt-4">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <MapPin size={20} className="text-primary" />
                  <h4 className="font-medium text-foreground">{t("office")}</h4>
                </div>
                <p className="text-sm text-muted-foreground">
                  {t("officeAddress")}
                </p>
                <Button variant="outline" size="sm" className="mt-3" asChild>
                  <a
                    href="https://maps.google.com/maps?q=Beni+Berman+2,+Netanya,+Israel"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("viewOnMap")}
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Testimonials */}
          <div className="lg:col-span-1">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-foreground">
                {t("clientTestimonials")}
              </h3>
            </div>

            {testimonialsLoading ? (
              <div className="space-y-4">
                {[1, 2].map((i) => (
                  <Card key={i}>
                    <CardContent className="p-4">
                      <div className="animate-pulse">
                        <div className="h-4 bg-gray-200 rounded mb-2"></div>
                        <div className="h-16 bg-gray-200 rounded mb-3"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {apiTestimonials.slice(0, 3).map((testimonial) => (
                  <Card key={testimonial.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-1 mb-3">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            size={14}
                            className={
                              i < testimonial.rating
                                ? "text-amber-400 fill-current"
                                : "text-gray-300"
                            }
                          />
                        ))}
                      </div>
                      <p className="text-sm text-muted-foreground mb-3 italic">
                        "{testimonial.message}"
                      </p>
                      <div className="text-xs text-muted-foreground">
                        <span className="font-medium">{testimonial.name}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {apiTestimonials.length === 0 && (
                  <Card>
                    <CardContent className="p-4 text-center text-muted-foreground">
                      {t("noTestimonials")}
                    </CardContent>
                  </Card>
                )}

                <TestimonialDialog />
              </div>
            )}
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <Card className="bg-gradient-to-r from-primary/5 to-accent/5 border-primary/20">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-foreground mb-4">
                {t("readyToStart")}
              </h3>
              <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                {t("readyToStartDescription")}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  size="lg"
                  className="bg-accent hover:bg-accent/90"
                  asChild
                >
                  <a href="tel:+972502364585">
                    <Phone size={18} className="mr-2" />
                    {t("callNow")}
                  </a>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <a href="https://wa.me/972502364585">
                    <MessageCircle size={18} className="mr-2" />
                    {t("whatsapp")}
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-border bg-muted/30">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-muted-foreground text-center md:text-left">
              <div>{t("allRightsReserved")}</div>
              <div className="mt-1 flex items-center justify-center md:justify-start gap-2">
                <span>{t("developedBy")}</span>
                <a
                  href="https://www.na-systems.tech/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center hover:opacity-80 transition-opacity"
                >
                  <img 
                    src={NASysetmsLogo} 
                    alt="NA-Systems" 
                    className="h-5 w-auto"
                  />
                </a>
              </div>
            </div>
            <div className="flex gap-6 text-sm">
              <button
                onClick={() => handleNavigation("/privacy-policy")}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("privacyPolicy")}
              </button>
              <button
                onClick={() => handleNavigation("/terms-of-use")}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("termsOfService")}
              </button>
              <button
                onClick={() => handleNavigation("/accessibility-statement")}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("accessibilityStatement")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
